/**
 * share-wc: Social share component with native Web Share API and platform fallbacks
 *
 * Three-tier progressive enhancement:
 * - Tier 1 (native): Single "Share" button using navigator.share()
 * - Tier 2 (platforms): Individual platform buttons with share URLs
 * - Tier 3 (no JS): Static <a> links in light DOM work as-is
 *
 * Inside <selection-menu> it renders one icon button that shares (or copies)
 * the selected text as a quote with a link back to the resolved URL.
 *
 * @attr {string} url              - URL to share (default: canonical link, then location.href)
 * @attr {string} title            - Share title (default: og:title, then document.title)
 * @attr {string} text             - Share description (default: meta description, then og:description)
 * @attr {string} platforms        - Comma-separated platform list (default: "x,linkedin,bluesky,mastodon,whatsapp,email,copy")
 * @attr {string} variant          - Visual variant: "icon", "label", "icon-label" (default: "icon-label")
 * @attr {string} size             - Button size: "s", "m", "l" (default: "m")
 * @attr {string} label            - Visible label for the native share button (default: "Share")
 * @attr {boolean} color           - Present = brand colours on each platform's border and icon
 * @attr {string} mastodon-instance - Mastodon instance (default: "mastodon.social")
 * @attr {string} tier             - Force tier: "native", "platforms" (overrides auto-detection)
 * @attr {string} data-label-share-page      - Name of the share nav (default "Share this page")
 * @attr {string} data-label-share-on        - Platform button name, {platform} token (default "Share on {platform}")
 * @attr {string} data-label-copy            - Copy button label (default "Copy link")
 * @attr {string} data-label-copied          - Visible copy confirmation (default "Copied!")
 * @attr {string} data-label-link-copied     - Announced after copying the link (default "Link copied to clipboard")
 * @attr {string} data-label-share-selection - Selection-menu button name (default "Share selection")
 * @attr {string} data-label-selection-copied - Announced after copying a selection (default "Selection copied")
 *
 * @fires share-wc:open    - detail { platform }
 * @fires share-wc:success - detail { platform }
 * @fires share-wc:error   - detail { platform, error? }
 *
 * @example
 * <share-wc></share-wc>
 *
 * @example Force platform buttons even on devices with Web Share API
 * <share-wc tier="platforms" platforms="x,linkedin,copy"></share-wc>
 */

import { registerComponent } from '../../lib/bundle-registry.js';
import { VBElement } from '../../lib/vb-element.js';
import { copyText } from '../../utils/copy-init.js';
import { PLATFORMS, DEFAULT_PLATFORMS } from './platforms.js';

const LABELS = {
  sharePage: 'Share this page',
  shareOn: 'Share on {platform}',
  copy: 'Copy link',
  copied: 'Copied!',
  linkCopied: 'Link copied to clipboard',
  shareSelection: 'Share selection',
  selectionCopied: 'Selection copied',
};

const TOKEN_RE = /\{(url|title|text)\}/;

/**
 * Build a scroll-to-text fragment for a quote.
 * @param {string} url
 * @param {string} text
 */
function withTextFragment(url, text) {
  const enc = (/** @type {string} */ s) => encodeURIComponent(s).replace(/-/g, '%2D');
  const words = text.replace(/\s+/g, ' ').trim().split(' ');
  const directive = words.length > 10
    ? `${enc(words.slice(0, 4).join(' '))},${enc(words.slice(-4).join(' '))}`
    : enc(words.join(' '));
  return `${url.split('#')[0]}#:~:text=${directive}`;
}

class ShareWc extends VBElement {
  /** @type {string | undefined} */
  #url;
  /** @type {string | undefined} */
  #title;
  /** @type {string | undefined} */
  #text;
  /** Values set from JS; they win over attributes and page metadata and survive reconnect. */
  /** @type {{ url?: string, title?: string, text?: string }} */
  #overrides = {};
  /** Original href templates of slotted links (tokens intact). @type {WeakMap<Element, string>} */
  #templates = new WeakMap();
  /** @type {string} */
  #tier = '';
  /** @type {ReturnType<typeof setTimeout> | null} */
  #copyTimer = null;
  /** @type {HTMLElement} */
  #nav = /** @type {*} */ (null);
  /** Whether #nav was generated by #renderPlatforms (vs. the author's slotted nav). */
  #navGenerated = false;

  /**
   * URL that is shared (property beats the url attribute and the canonical link).
   * @returns {string | undefined}
   */
  get url() {
    return this.#overrides.url ?? this.#url;
  }

  set url(val) {
    this.#setOverride('url', val);
  }

  /**
   * Share title. Named shareTitle so it doesn't shadow HTMLElement.title
   * (the tooltip attribute); the `title` attribute still configures it.
   * @returns {string}
   */
  get shareTitle() {
    return this.#overrides.title ?? this.#title ?? '';
  }

  /** @param {string} val */
  set shareTitle(val) {
    this.#setOverride('title', val);
  }

  get text() {
    return this.#overrides.text ?? this.#text;
  }

  set text(val) {
    this.#setOverride('text', val);
  }

  /**
   * @param {'url' | 'title' | 'text'} key
   * @param {string | null | undefined} val
   */
  #setOverride(key, val) {
    if (val == null) delete this.#overrides[key];
    else this.#overrides[key] = String(val);
    if (!this.hasAttribute('data-upgraded')) return;
    this.#resolveMeta();
    this.#substituteTokens();
  }

  setup() {
    this.#resolveMeta();
    this.#detectTier();

    // Inside selection-menu: render minimal icon button
    if (this.closest('selection-menu')) {
      this.#renderSelectionAction();
      this.setAttribute('data-tier-resolved', 'selection');
      return;
    }

    if (this.#tier === 'native') {
      this.#renderNative();
    } else if (this.#tier === 'hidden') {
      this.style.display = 'none';
    } else if (this.children.length > 0) {
      this.#enhanceSlotted();
    } else {
      this.#renderPlatforms();
    }

    this.setAttribute('data-tier-resolved', this.#tier);
  }

  teardown() {
    this.removeAttribute('data-tier-resolved');
    if (this.#tier === 'hidden') this.style.removeProperty('display');
    if (this.#copyTimer) {
      clearTimeout(this.#copyTimer);
      this.#copyTimer = null;
    }
    for (const label of this.querySelectorAll('.share-label[data-original]')) {
      label.textContent = /** @type {HTMLElement} */ (label).dataset.original ?? '';
      delete /** @type {HTMLElement} */ (label).dataset.original;
    }
    if (this.#nav) {
      this.#nav.removeEventListener('click', this.#handlePlatformClick);
      // Only remove a nav we generated (#renderPlatforms). The slotted nav
      // (#enhanceSlotted) holds the author's links; setup() reuses it on
      // reconnect, so removing it would lose that content.
      if (this.#navGenerated) this.#nav.remove();
      this.#nav = /** @type {*} */ (null);
      this.#navGenerated = false;
    }
    // Remove the native share / selection buttons if they were rendered, so a
    // reconnect's setup() does not append a duplicate.
    this.querySelector(':scope > .share-trigger')?.remove();
    this.querySelector(':scope > .share-selection')?.remove();
  }

  /**
   * Share programmatically. Uses the native share sheet in the native tier
   * (copying the link if the share fails); in every other tier it copies
   * the link. Resolves true when something was shared or copied.
   * @returns {Promise<boolean>}
   */
  async share() {
    if (this.#tier === 'native') return this.#doNativeShare();
    return this.#copyLink();
  }

  /** @param {keyof typeof LABELS} key */
  #label(key) {
    const attr = `data-label-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
    return this.getAttribute(attr) || LABELS[key];
  }

  #resolveMeta() {
    // URL: property → attribute → canonical → location
    this.#url = this.#overrides.url
      || this.getAttribute('url')
      || /** @type {HTMLLinkElement | null} */ (document.querySelector('link[rel~="canonical" i]'))?.href
      || location.href;

    // Title: property → attribute → og:title → document.title
    this.#title = this.#overrides.title
      ?? (this.getAttribute('title')
      || document.querySelector('meta[property="og:title"]')?.getAttribute('content')
      || document.title);

    // Text: property → attribute → meta description → og:description → empty
    this.#text = this.#overrides.text
      ?? (this.getAttribute('text')
      || document.querySelector('meta[name="description"]')?.getAttribute('content')
      || document.querySelector('meta[property="og:description"]')?.getAttribute('content')
      || '');
  }

  #payload() {
    return { url: this.#url || '', title: this.#title || '', text: this.#text || '' };
  }

  #detectTier() {
    // Explicit override: tier="platforms" forces Tier 2
    const override = this.getAttribute('tier');
    if (override === 'platforms' || override === 'native') {
      if (override === 'native' && !navigator.share) {
        this.#tier = 'hidden';
      } else {
        this.#tier = override;
      }
      return;
    }

    const platforms = this.getAttribute('platforms') || DEFAULT_PLATFORMS;

    if (platforms === 'native-only') {
      this.#tier = typeof navigator.share === 'function' ? 'native' : 'hidden';
      return;
    }

    this.#tier = typeof navigator.share === 'function' ? 'native' : 'platforms';
  }

  /** Visible label span (textContent — never markup). @param {string} text */
  #labelSpan(text) {
    const span = document.createElement('span');
    span.className = 'share-label';
    span.textContent = text;
    return span;
  }

  /**
   * @param {string} name
   * @param {string} [set]
   */
  #icon(name, set) {
    const icon = document.createElement('icon-wc');
    icon.setAttribute('name', name);
    icon.setAttribute('size', 'sm');
    if (set) icon.setAttribute('set', set);
    icon.setAttribute('aria-hidden', 'true');
    return icon;
  }

  #renderSelectionAction() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'share-selection';
    btn.setAttribute('aria-label', this.#label('shareSelection'));
    btn.appendChild(this.#icon('share-2'));
    btn.addEventListener('click', async () => {
      const menu = /** @type {any} */ (this.closest('selection-menu'));
      const sel = menu?.getSelection?.();
      const text = sel?.text || window.getSelection()?.toString().trim() || '';
      if (!text) {
        menu?.dismiss?.();
        return;
      }
      const link = withTextFragment(this.#url || location.href, text);
      const quote = `"${text}" — ${link}`;
      let shared = false;
      if (typeof navigator.share === 'function') {
        try {
          await navigator.share({ text: quote });
          shared = true;
        } catch (err) {
          // The user cancelled: nothing to fall back to.
          if (/** @type {Error} */ (err)?.name === 'AbortError') shared = true;
        }
      }
      // Announce through a document-level live region (no button): the
      // toolbar is hidden by the dismiss below, which would silence it.
      if (!shared) await copyText(quote, { announceMessage: this.#label('selectionCopied') });
      menu?.dismiss?.();
    });
    this.appendChild(btn);
  }

  #renderNative() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'share-trigger';
    // The visible (localizable) label is the accessible name — no aria-label.
    btn.append(this.#icon('share-2'), this.#labelSpan(this.getAttribute('label') || 'Share'));
    btn.addEventListener('click', this.#handleNativeShare);
    this.appendChild(btn);
  }

  #renderPlatforms() {
    const platformList = (this.getAttribute('platforms') || DEFAULT_PLATFORMS)
      .split(',')
      .map(p => p.trim())
      .filter(Boolean);

    this.#nav = document.createElement('nav');
    this.#nav.setAttribute('aria-label', this.#label('sharePage'));

    for (const id of platformList) {
      const platform = PLATFORMS.get(id);
      if (!platform) continue;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.platform = id;
      if (id === 'copy') {
        btn.append(this.#icon(platform.iconName, platform.iconSet), this.#labelSpan(this.#label('copy')));
      } else {
        // The name contains the visible platform label (WCAG 2.5.3).
        btn.setAttribute('aria-label', this.#label('shareOn').replace('{platform}', platform.label));
        btn.append(this.#icon(platform.iconName, platform.iconSet), this.#labelSpan(platform.label));
      }

      this.#nav.appendChild(btn);
    }

    this.#nav.addEventListener('click', this.#handlePlatformClick);
    this.appendChild(this.#nav);
    this.#navGenerated = true;
  }

  #enhanceSlotted() {
    // Wrap in nav if not already wrapped
    if (!this.querySelector('nav')) {
      const nav = document.createElement('nav');
      nav.setAttribute('aria-label', this.#label('sharePage'));
      while (this.firstChild) {
        nav.appendChild(this.firstChild);
      }
      this.appendChild(nav);
      this.#nav = nav;
    } else {
      this.#nav = /** @type {HTMLElement} */ (this.querySelector('nav'));
    }

    this.#substituteTokens();

    // Copy buttons are handled by the delegated nav listener.
    this.#nav.addEventListener('click', this.#handlePlatformClick);
  }

  /** Substitute {url}/{title}/{text} in slotted links from their original templates. */
  #substituteTokens() {
    const { url, title, text } = this.#payload();
    for (const link of this.querySelectorAll('a[data-platform]')) {
      let template = this.#templates.get(link);
      if (template == null) {
        const href = link.getAttribute('href') || '';
        if (!TOKEN_RE.test(href)) continue;
        template = href;
        this.#templates.set(link, template);
      }
      link.setAttribute('href', template
        .replace(/\{url\}/g, encodeURIComponent(url))
        .replace(/\{title\}/g, encodeURIComponent(title))
        .replace(/\{text\}/g, encodeURIComponent(text)));
    }
  }

  #handleNativeShare = async () => {
    await this.#doNativeShare();
  };

  /** @returns {Promise<boolean>} */
  async #doNativeShare() {
    const payload = this.#payload();

    this.dispatchEvent(new CustomEvent('share-wc:open', {
      detail: { platform: 'native' },
      bubbles: true,
    }));

    try {
      if (navigator.canShare && !navigator.canShare(payload)) {
        throw new Error('Payload not shareable');
      }
      await navigator.share(payload);
      this.dispatchEvent(new CustomEvent('share-wc:success', {
        detail: { platform: 'native' },
        bubbles: true,
      }));
      return true;
    } catch (err) {
      if (/** @type {Error} */ (err)?.name === 'AbortError') return false;
      this.dispatchEvent(new CustomEvent('share-wc:error', {
        detail: { platform: 'native', error: err },
        bubbles: true,
      }));
      // Native share refused (NotAllowedError, unshareable payload…):
      // fall back to copying the link so the user still gets something.
      return this.#copyLink(this.querySelector(':scope > .share-trigger'));
    }
  }

  #handlePlatformClick = (/** @type {Event} */ e) => {
    const btn = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (e.target).closest('[data-platform]'));
    if (!btn) return;

    const id = btn.dataset.platform;

    if (id === 'copy') {
      if (btn.tagName === 'A') e.preventDefault();
      this.#copyLink(btn);
      return;
    }

    // For slotted <a> elements, let the default action happen
    if (btn.tagName === 'A') return;

    if (!id) return;
    const platform = PLATFORMS.get(id);
    if (!platform) return;

    this.dispatchEvent(new CustomEvent('share-wc:open', {
      detail: { platform: id },
      bubbles: true,
    }));

    const opts = this.#payload();

    if (id === 'email') {
      location.href = platform.buildUrl(opts);
    } else if (id === 'mastodon') {
      const instance = this.getAttribute('mastodon-instance') || 'mastodon.social';
      const url = /** @type {(opts: { url: string, title: string, text: string }, instance?: string) => string} */ (platform.buildUrl)(opts, instance);
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      window.open(platform.buildUrl(opts), '_blank', 'noopener,noreferrer');
    }

    this.dispatchEvent(new CustomEvent('share-wc:success', {
      detail: { platform: id },
      bubbles: true,
    }));
  };

  /**
   * Copy the resolved URL, swap the button's visible label briefly and
   * announce. Works without a button (share() / native fallback).
   * @param {Element | null} [btn]
   * @returns {Promise<boolean>}
   */
  async #copyLink(btn = null) {
    const button = /** @type {HTMLElement | undefined} */ (btn?.isConnected ? btn : undefined);
    const ok = await copyText(this.#url || '', {
      button,
      announceMessage: this.#label('linkCopied'),
      duration: 2000,
    });

    if (!ok) {
      this.dispatchEvent(new CustomEvent('share-wc:error', {
        detail: { platform: 'copy' },
        bubbles: true,
      }));
      return false;
    }

    this.dispatchEvent(new CustomEvent('share-wc:success', {
      detail: { platform: 'copy' },
      bubbles: true,
    }));

    // Visible label swap (data-state set by copyText drives any CSS hook)
    const label = /** @type {HTMLElement | null} */ (
      button?.dataset.platform === 'copy' ? button.querySelector('.share-label') : null);
    if (!label) return true;
    const original = label.dataset.original ?? label.textContent ?? '';
    label.dataset.original = original;
    label.textContent = this.#label('copied');

    if (this.#copyTimer) clearTimeout(this.#copyTimer);
    this.#copyTimer = setTimeout(() => {
      label.textContent = original;
      delete label.dataset.original;
      this.#copyTimer = null;
    }, 2000);
    return true;
  }
}

registerComponent('share-wc', /** @type {typeof HTMLElement} */ (/** @type {unknown} */ (ShareWc)));

export { ShareWc };
